/**
 * src/shell/ui/screens/PhoneScreen.tsx — S4 电话 · 部门通话屏（设计稿 §1.2 S4）。
 *
 * 拨号盘四键 → 部门通话屏（共用骨架：部门名 + 报表区 + 挂电话）。
 * 财务/市场/项目 = 只读报表；人事部含招聘/解雇（Phase A 既有操作）。
 * 项目部屏查看即标记已读（外壳本地 last-read，评审对齐项 3）。
 * 拨号盘层 mount = 全段已读（接电话即已读，2026-08-15 拍板：缺陷 A+B 修复）。
 */

import { useEffect } from 'react'
import { COPY } from '../../copy'
import { fireEmployee, hireCandidate } from '../../gm-client/actions'
import { useProjectEvents, useRunState, useSave } from '../../gm-client/hooks'
import { markAllSegmentsRead, markRead } from '../../save/unread'
import { EventFeed } from '../components/EventFeed'
import { DESK, type Dept, type Screen } from '../nav'

const C = COPY.phone

const DEPT_LABEL: Record<Dept, string> = {
  finance: C.finance,
  hr: C.hr,
  market: C.market,
  project: C.project,
}

export function PhoneScreen({ dept, go }: { dept?: Dept; go: (s: Screen) => void }) {
  const { bundle } = useSave()

  // 接电话即已读（缺陷 A+B 修复，2026-08-15 拍板）：拨号盘层 mount 时
  // 把全局段 + 在研项目段一并标记已读。只在拨号盘层做；子屏行为不变。
  // mount 之后新到的事件不算已读，红灯会再亮（这正是设计）。
  useEffect(() => {
    if (!dept) markAllSegmentsRead(bundle.projects.projects)
    // 依赖只挂 dept：进入拨号盘层的那次 mount 触发一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dept])

  return (
    <div className="screen deep-screen">
      <button className="btn back-btn" type="button" onClick={() => go(DESK)}>
        {COPY.nav.hangUp}
      </button>
      {!dept ? (
        <div className="panel">
          <h3>{C.dialTitle}</h3>
          <div className="dial-pad">
            {(Object.keys(DEPT_LABEL) as Dept[]).map((d) => (
              <button key={d} type="button" className="btn dial-key" onClick={() => go({ name: 'phone', dept: d })}>
                {DEPT_LABEL[d]}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="panel">
          <h3>{DEPT_LABEL[dept]}</h3>
          {dept === 'finance' && <FinanceDept />}
          {dept === 'hr' && <HrDept />}
          {dept === 'market' && <MarketDept />}
          {dept === 'project' && <ProjectDept />}
        </div>
      )}
    </div>
  )
}

// ── 财务部（只读：资金/成本结构/低保记录）─────────────────────────────────

function FinanceDept() {
  const { bundle } = useSave()
  const save = bundle.save
  return (
    <div>
      <h4>{C.financeDept.title}</h4>
      <table className="ledger-table">
        <tbody>
          <tr><td>{COPY.computer.ledger.funds}</td><td className="mono">{save.funds}</td></tr>
          <tr><td>{COPY.computer.ledger.tokenTotal}</td><td className="mono">{save.total_token_cost}</td></tr>
          <tr><td>{COPY.computer.ledger.bailout}</td><td className="mono">{save.bailout.times_used}</td></tr>
        </tbody>
      </table>
    </div>
  )
}

// ── 人事部（在职 + 人才市场 + 招聘/解雇）──────────────────────────────────

function HrDept() {
  const { bundle, candidates } = useSave()
  const H = C.hrDept
  const hired = bundle.employees.employees
  return (
    <div>
      <h4>{H.hired}</h4>
      {hired.map((e) => (
        <div key={e.id} className="list-row">
          <span className="list-title">
            {e.name} · {e.role} · <span className={`badge rarity-${e.rarity}`}>{e.rarity}</span>
          </span>
          <span className="dim">{e.status === 'in_project' ? H.inProject : e.catchphrase}</span>
          {e.status === 'hired' && (
            <button
              className="btn btn-danger btn-small"
              type="button"
              onClick={() => {
                if (window.confirm(H.fireConfirm(e.name))) void fireEmployee(e.id)
              }}
            >
              {H.fire}
            </button>
          )}
        </div>
      ))}
      <h4>{H.candidates}</h4>
      {candidates.length === 0 && <p className="dim">—</p>}
      {candidates.map((c) => {
        const roleFull = hired.some((e) => e.role === c.role)
        return (
          <div key={c.id} className="list-row">
            <span className="list-title">
              {c.name} · {c.role} · <span className={`badge rarity-${c.rarity}`}>{c.rarity}</span>
            </span>
            <span className="dim">{c.personality}</span>
            <button
              className="btn btn-primary btn-small"
              type="button"
              disabled={roleFull}
              title={roleFull ? H.roleFull : undefined}
              onClick={() => void hireCandidate(c.id)}
            >
              {H.hire}
            </button>
          </div>
        )
      })}
    </div>
  )
}

// ── 市场部（只读：当季风向）───────────────────────────────────────────────

function MarketDept() {
  const { bundle } = useSave()
  const m = bundle.market
  const M = C.marketDept
  return (
    <div>
      <h4>
        {M.season}：{m.season}
      </h4>
      <div className="market-cols">
        <div>
          <h5>{M.hot}</h5>
          <ul>{m.hot_themes.map((t) => <li key={t.tag}>{t.name}（{t.note}）</li>)}</ul>
        </div>
        <div>
          <h5>{M.cold}</h5>
          <ul>{m.cold_themes.map((t) => <li key={t.tag}>{t.name}（{t.note}）</li>)}</ul>
        </div>
        <div>
          <h5>{M.mechanics}</h5>
          <ul>{m.hot_mechanics.map((t) => <li key={t.tag}>{t.name}（{t.note}）</li>)}</ul>
        </div>
      </div>
      <h5>{M.editorial}</h5>
      <p>{m.editorial}</p>
    </div>
  )
}

// ── 项目部（在研项目进展 + 现场播报；查看即已读）─────────────────────────

function ProjectDept() {
  const { bundle } = useSave()
  const active = bundle.projects.projects
    .filter((p) => p.status !== 'completed')
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0]
  const events = useProjectEvents(active?.id ?? null)
  const runState = useRunState(active?.id ?? null)

  // 查看即标记已读（桌面电话灯随之熄灭）
  useEffect(() => {
    if (active && events.length > 0) {
      markRead(active.id, events[events.length - 1].seq)
    }
  }, [active, events])

  if (!active) return <p className="dim">{C.projectDept.noActive}</p>
  return (
    <div>
      <p>
        <span className="badge">{COPY.computer.status[active.status]}</span> {active.idea.slice(0, 40)}
      </p>
      {runState && (
        <p className="dim">
          {C.projectDept.phase}：{runState.phase}
          {runState.activeSession
            ? ` · ${runState.activeSession.stage} 第 ${runState.activeSession.rounds} 轮`
            : ''}
        </p>
      )}
      {runState && runState.queue.length > 0 && (
        <p className="dim">
          {C.projectDept.queue}：{runState.queue.map((r) => `${r.action}(${r.state})`).join('、')}
        </p>
      )}
      <h4>{C.projectDept.feed}</h4>
      <EventFeed events={events} />
    </div>
  )
}
